import { default as fetch } from '@n2-common/lib/fetchProxy';
import O2ApiMock from './mock/service';
import type * as O2 from './types';
import { throwIfNotSuccess } from '../errors';
import { LoanFiApplicationInputModel } from '@n2-common/serverGeneratedTypings/loanFiApplicationInputModel';
import { CreditCardFiApplicationInputModel } from '@n2-common/serverGeneratedTypings/creditCardFiApplicationInputModel';
import { CreditCardNoApplicationInputModel } from '@n2-common/serverGeneratedTypings/creditCardNoApplicationInputModel';
import { LoanNoApplicationInputModel } from '@n2-common/serverGeneratedTypings/loanNoApplicationInputModel';
import { SwedishLoanApplication, SwedishLoanApplicationCleaned } from '@n2-common/components/WizardFormManager/se/fieldDefinitions';
import { FinishLoanApplicationCleaned } from '@n2-common/components/WizardFormManager/fi/fieldDefinitions';
import { NorwegianLoanApplication } from '@n2-common/components/WizardFormManager/no/fieldDefinitions';
import { ConsentToSblModel } from '@n2-common/serverGeneratedTypings/o2_consentToSblModel';
import { ConsentToEdrModel } from '@n2-common/serverGeneratedTypings/o2_consentToEdrModel';
import { StartSigningProcessModel } from '@n2-common/serverGeneratedTypings/o2_startSigningProcessModel';
import { StartSigningProcessPayload as StartSigningProcessPayloadType } from '@n2-common/serverGeneratedTypings/o2_startSigningProcessPayload';
import { SelectAndAcceptOfferModel as SelectAndAcceptOfferModelType } from '@n2-common/serverGeneratedTypings/o2_selectAndAcceptOfferModel';
import { RefinanceSelection as PriceDebtRefinanceDetails } from '@n2-common/serverGeneratedTypings/o2_refinanceSelection';
import { VerifyAccountOwnerResult } from '@n2-common/serverGeneratedTypings/o2_verifyAccountOwnerResult';

/** exporting O2 types as namespace object */
export * as O2 from './types';
export * as O2Mappers from './mappers';

const O2Api = {
    async postApplication(
        application:
            | FinishLoanApplicationCleaned
            | SwedishLoanApplication
            | CreditCardFiApplicationInputModel
            | CreditCardNoApplicationInputModel
            | NorwegianLoanApplication,
        country: Country,
        type: 'loan' | 'creditcard',
        signal?: AbortSignal
    ) {
        const fetchConfig = {
            method: 'POST',
            body: JSON.stringify(application),
            signal,
            headers: {
                'Content-Type': 'application/json',
                ...maybeScenario(),
            },
        };
        const response = await fetch(`/api/n2/o2-application/${country}/${type}/application`, fetchConfig);
        await throwIfNotSuccess(response, 'O2Service.PostApplication', fetchConfig);
        return await response.json();
    },
    async postCustomerLoanIncreaseApplication(
        application: FinishLoanApplicationCleaned | SwedishLoanApplicationCleaned | LoanNoApplicationInputModel,
        country: Country,
        signal?: AbortSignal
    ) {
        const fetchConfig = {
            method: 'POST',
            body: JSON.stringify(application),
            signal,
            headers: {
                'Content-Type': 'application/json',
                ...maybeScenario(),
            },
        };
        const response = await fetch(`/api/n2/o2-application/${country}/customer-loan/application`, fetchConfig);
        await throwIfNotSuccess(response, 'O2Service.PostCustomerLoanIncreaseApplication', fetchConfig);
        return await response.json();
    },
    async status(applicationId: string, applicantId: string | null, signal?: AbortSignal) {
        const applicantUrl = applicantId ? `/${applicantId}` : '';
        const response = await fetch(`/api/n2/o2-application/status/${applicationId}${applicantUrl}`, {
            method: 'GET',
            signal,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        await throwIfNotSuccess(response, 'O2Service.Status');
        if (response.status == 200) {
            return (await response.json()) as O2.ApplicationStatus | { status: 'Redirect'; applicationId: string; applicantId: string };
        } else {
            return null;
        }
    },
    async userTaskComplete(taskId: string, payload: UserTaskModel, signal?: AbortSignal) {
        const response = await fetch(`/api/n2/o2-application/user-task-complete/${taskId}`, {
            method: 'POST',
            body: JSON.stringify(payload),
            signal,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        await throwIfNotSuccess(response, 'O2Service.UserTaskComplete');
    },

    async submitConsents(taskId: string, consentName: string, consents: string[], signal?: AbortSignal) {
        const response = await fetch(`/api/n2/o2-application/consent/${taskId}/${consentName}`, {
            method: 'PUT',
            body: JSON.stringify(consents),
            signal,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        await throwIfNotSuccess(response, 'O2Service.SubmitConsents');
    },

    async uploadDocuments(applicationId: string, role: O2.ApplicantRole, documentType: string, form: FormData, signal?: AbortSignal) {
        const url = `/api/n2/o2-application/upload-documents/${applicationId}/${role}/${documentType}`;
        const response = await fetch(url, {
            method: 'POST',
            body: form,
            signal,
        });
        await throwIfNotSuccess(response, 'O2Service.UploadDocuments');
        return (await response.json()) as O2.DocumentInfo[];
    },
    async deleteDocument(applicationId: string, role: O2.ApplicantRole, documentId: string, signal?: AbortSignal) {
        const response = await fetch(`/api/n2/o2-application/delete-document/${applicationId}/${role}/${documentId}`, {
            method: 'DELETE',
            signal,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        await throwIfNotSuccess(response, 'O2Service.DeleteDocument');
    },
    async lookupBankName(country: Country, accountNumber: string, signal?: AbortSignal): Promise<O2.BankInfo | null> {
        const response = await fetch(`/api/n2/o2-application/payoutbank/${country}/${accountNumber}`, {
            method: 'GET',
            signal,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        await throwIfNotSuccess(response, 'O2Service.LookupBankName');
        return response.status == 200 ? ((await response.json()) as O2.BankInfo) : null;
    },

    async getEligibility(product: CustomerEngagementProduct) {
        const response = await fetch(`/api/n2/o2-application/eligibility/${product}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        await throwIfNotSuccess(response, 'O2Service.GetEligibility');
        return { [product]: (await response.json()) as boolean } as Eligibility<typeof product>;
    },

    async getSetPinUrl(taskId: string, requestReference: string, cardHolderIdentifier: string, signal?: AbortSignal) {
        const response = await fetch(`/api/n2/o2-application/set-pin-url/${taskId}/${requestReference}/${cardHolderIdentifier}`, {
            method: 'GET',
            signal,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        await throwIfNotSuccess(response, 'O2Service.GetSetPinUrl');
        if (response.status == 200) {
            return await response.json();
        } else {
            return null;
        }
    },

    async runPriceCalculation(applicationId: string, debts: PropertyNaming<PriceDebtRefinanceDetails, 'CamelCase'>[], signal?: AbortSignal) {
        const response = await fetch(`/api/n2/o2-application/${applicationId}/run-price`, {
            method: 'POST',
            signal,
            body: JSON.stringify(debts),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        await throwIfNotSuccess(response, 'O2Service.RunPriceCalculation');
        if (response.status == 200) {
            return (await response.json()) as PriceCalculation;
        } else {
            return null;
        }
    },
    async getAccountOwnerVerification(applicationId: string, accountNUmber: string, signal?: AbortSignal) {
        const response = await fetch(`/api/n2/o2-application/${applicationId}/verify-account-owner/${accountNUmber}`, {
            method: 'GET',
            signal,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        await throwIfNotSuccess(response, 'O2Service.GetAccountOwnerVerification');
        if (response.status == 200) {
            return (await response.json()) as PropertyNaming<VerifyAccountOwnerResult, 'CamelCase'>;
        } else {
            return null;
        }
    },
};

export type PriceCalculation = {
    offers?: O2.DecisionOffer[];
    refinanceInfoSets?: O2.RefinanceInfoSet[];
};

export type Tracking = LoanFiApplicationInputModel['Tracking'];

export type SigningProcessStartPayload = PropertyNaming<StartSigningProcessPayloadType, 'CamelCase'>;

type AmlQuestion = {
    id: number;
    ref: string;
    hint: string;
    response: string;
};
type AmlQuestionsResponsePayload = {
    questions: AmlQuestion[];
    scope: O2.QuestionScope;
};
export type StoreAccountNumberModel = {
    accountNumber?: string;
    details?: Detail[];
};

type Detail = {
    name: string;
    type: 'consumerLoan' | 'creditCard' | 'unsecuredDebt';
    amount: number;
    accountNumber: string;
    ref?: string;
    refType: 'Message' | 'KID' | 'None';
};
export type SelectAndAcceptOfferModel = PropertyNaming<SelectAndAcceptOfferModelType, 'CamelCase'>;
export type UserTaskModel =
    | PropertyNaming<ConsentToSblModel, 'CamelCase'>
    | PropertyNaming<ConsentToEdrModel, 'CamelCase'>
    | { type: 'upload-documents' }
    | { type: 'set-pin' }
    | { type: 'accept-offer-cosigner' }
    | { type: 'store-aml-questions'; payload: AmlQuestionsResponsePayload }
    | PropertyNaming<StartSigningProcessModel, 'CamelCase'>
    | { type: 'select-embossed-name'; payload: string }
    | { type: 'enter-account-number'; payload: StoreAccountNumberModel }
    | SelectAndAcceptOfferModel;

export type O2ApiService = typeof O2Api;

export type CustomerEngagementProduct = 'CreditCard' | 'Deposit' | 'ConsumerLoan';
export type Eligibility<T extends CustomerEngagementProduct> = { [key in T]: boolean };

export const o2ApiService: O2ApiService = process.env.NODE_ENV == 'production' ? O2Api : O2ApiMock;

export default o2ApiService;

function maybeScenario(): HeadersInit | undefined {
    const Scenario = localStorage.getItem('Scenario') || localStorage.getItem('scenario');
    if(Scenario){
        console.warn(...Scenario.split(','))
    }
    return Scenario ? { Scenario } : undefined;
}
